import * as React from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { formatAmount, formatDate } from "@/lib/format"
import { reportWriteError } from "@/lib/writeErrors"
import { cn } from "@/lib/utils"

import { considerarFactura } from "@/routes/finance/api"
import { CONSIDERAR_EN, nombrePais } from "@/routes/finance/mapping"
import type { ConsiderarEn, Factura } from "@/routes/finance/mapping"
import { t } from "@/lib/i18n"

/**
 * Donde cuenta una factura.
 *
 * Es lo UNICO editable de una factura importada, y es deliberado: el importe, el
 * numero y el cliente son lo que la sociedad emitio, y corregirlos aca dejaria
 * el sistema diciendo algo distinto de lo que dice el comprobante. Lo que si es
 * una decision —y hasta hoy solo se podia tomar por SQL— es en que lado cuenta.
 *
 * EL MOTIVO ES OBLIGATORIO PARA EXCLUIR Y NO PARA VOLVER. Sacar una factura de
 * un lado mueve una cifra que alguien va a leer despues; sin explicacion, esa
 * cifra deja de ser una correccion y pasa a ser una perdida. Volver al caso
 * normal no mueve nada que haya que justificar, y ademas LIMPIA el motivo
 * anterior: una explicacion de una exclusion que ya no existe es una
 * explicacion que miente.
 */
export function ConsiderarFacturaDialog({
  factura, open, onOpenChange,
}: {
  factura: Factura | null
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [destino, setDestino] = React.useState<ConsiderarEn>("ambos")
  const [motivo, setMotivo] = React.useState("")

  React.useEffect(() => {
    if (factura) {
      setDestino(factura.considerar_en)
      setMotivo(factura.motivo_exclusion ?? "")
    }
  }, [factura])

  const guardar = useMutation({
    mutationFn: () => considerarFactura(factura!.id, destino, motivo),
    onSuccess: () => {
      toast({
        variant: "success",
        title: "Guardado",
        description: "La conciliación del país ya lo refleja.",
      })
      for (const k of ["finance-conciliacion", "finance-facturas"]) {
        void queryClient.invalidateQueries({ queryKey: [k] })
      }
      onOpenChange(false)
    },
    onError: (e) =>
      reportWriteError(e, { entidad: "la factura", operacion: "editar" }),
  })

  if (!factura) return null

  const excluye = destino !== "ambos"
  const puedeGuardar =
    (!excluye || motivo.trim().length >= 3) && destino !== factura.considerar_en

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("Dónde cuenta esta factura")}</DialogTitle>
          <DialogDescription>
            {factura.numero} · {nombrePais(factura.pais)} · {factura.cliente}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 rounded-md border bg-surface-sunken/50 p-2 text-2xs">
            <dt className="text-muted-foreground">{t("Importe")}</dt>
            <dd className="num text-right font-medium">
              {formatAmount(factura.importe_usd)}{" "}{t("USD")}</dd>
            <dt className="text-muted-foreground">{t("Emitida")}</dt>
            <dd className="text-right">{formatDate(factura.fecha_emision) || "—"}</dd>
            <dt className="text-muted-foreground">{t("Estado")}</dt>
            <dd className="text-right">{factura.estado}</dd>
          </dl>

          <div className="space-y-1">
            <Label id="considerar-label">{t("Cuenta en")}</Label>
            <div role="radiogroup" aria-labelledby="considerar-label"
                 className="grid gap-1 sm:grid-cols-2">
              {CONSIDERAR_EN.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={destino === c.id}
                  onClick={() => setDestino(c.id)}
                  className={cn(
                    "rounded-md border p-2 text-left transition-colors duration-100",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    destino === c.id
                      ? "border-brand/50 bg-brand-soft"
                      : "hover:border-border-strong",
                  )}
                >
                  <div className="text-xs font-medium">{c.label}</div>
                  <div className="mt-0.5 text-2xs leading-4 text-muted-foreground">
                    {c.ayuda}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {excluye ? (
            <div className="space-y-1">
              <Label htmlFor="motivo-factura">{t("Motivo")}</Label>
              <Textarea
                id="motivo-factura" rows={2} value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder={t("Por qué sale de ese lado. Queda en el audit log y se ve en la tabla.")}
              />
              <p className="text-2xs text-muted-foreground">
                {t("Obligatorio: esta factura va a dejar de sumar y alguien va a leer el total sin saber por qué.")}</p>
            </div>
          ) : (
            <p className="rounded-md border border-dashed p-2 text-2xs leading-4 text-muted-foreground">
              {t("Vuelve a contar en los dos lados. El motivo anterior se borra: una explicación de una exclusión que ya no existe es una explicación que miente.")}</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t("Cancelar")}</Button>
          <Button
            size="sm"
            onClick={() => guardar.mutate()}
            disabled={!puedeGuardar || guardar.isPending}
          >
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
